import type {Resource, ResourceOptions, ResourceRef, Signal, ValueEqualityFn, WritableResource} from "@angular/core";
import {computed, resource} from "@angular/core";
import type {RxResourceOptions} from "@angular/core/rxjs-interop";
import {rxResource} from "@angular/core/rxjs-interop";
import type {Writable} from "type-fest";
import {equals} from "./Equatable";

interface _SafeValueResourceBase<T> {
  readonly valueOrDefault: Signal<T>;
}

export type SafeValueResource<T> = Resource<T> & _SafeValueResourceBase<T>;
export type SafeValueWritableResource<T> = WritableResource<T> & _SafeValueResourceBase<T>;
export type SafeValueResourceRef<T> = ResourceRef<T> & _SafeValueResourceBase<T>;

export function isSafeResource<T>(resource: ResourceRef<T>): resource is SafeValueResourceRef<T>;
export function isSafeResource<T>(resource: WritableResource<T>): resource is SafeValueWritableResource<T>;
export function isSafeResource<T>(resource: Resource<T>): resource is SafeValueResource<T>;
export function isSafeResource<T>(resource: Resource<T>): resource is SafeValueResource<T> {
  return "valueOrDefault" in resource;
}

function makeSafeValueResourceRef<T>(resourceRef: ResourceRef<T>,
                                     defaultValue: NoInfer<T>, equalFn?: ValueEqualityFn<NoInfer<T>>): SafeValueResourceRef<T>;
function makeSafeValueResourceRef<T>(resourceRef: ResourceRef<T>,
                                     defaultValue?: NoInfer<T>, equalFn?: ValueEqualityFn<NoInfer<Exclude<T, undefined>>>): SafeValueResourceRef<T | undefined>;
function makeSafeValueResourceRef<T>(resourceRef: ResourceRef<T>,
                                     defaultValue?: NoInfer<T>, equalFn?: ValueEqualityFn<NoInfer<Exclude<T, undefined>>>): SafeValueResourceRef<T | undefined> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const safeResourceRef = resourceRef as Writable<SafeValueResourceRef<T | undefined>, "valueOrDefault">;
  safeResourceRef.valueOrDefault = computed(
    () => resourceRef.hasValue() ? resourceRef.value() : defaultValue,
    {equal: equalFn !== undefined ? (a, b): boolean => equals(a, b, equalFn) : undefined},
  );
  return safeResourceRef;
}

export function safeResource<T, R>(opts: Readonly<ResourceOptions<T, R>> & {
  readonly defaultValue: NoInfer<T>;
}): SafeValueResourceRef<T>;
export function safeResource<T, R>(opts: Readonly<ResourceOptions<T, R>>): SafeValueResourceRef<T | undefined>
export function safeResource<T, R>(opts: Readonly<ResourceOptions<T, R>>): SafeValueResourceRef<T | undefined> {
  return makeSafeValueResourceRef(resource(opts), opts.defaultValue, opts.equal);
}

export function rxSafeResource<T, R>(opts: Readonly<RxResourceOptions<T, R>> & {
  readonly defaultValue: NoInfer<T>;
}): SafeValueResourceRef<T>;
export function rxSafeResource<T, R>(opts: Readonly<RxResourceOptions<T, R>>): SafeValueResourceRef<T | undefined>
export function rxSafeResource<T, R>(opts: Readonly<RxResourceOptions<T, R>>): SafeValueResourceRef<T | undefined> {
  return makeSafeValueResourceRef(rxResource(opts), opts.defaultValue, opts.equal);
}
